import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaCode, FaPalette, FaRocket, FaUsers } from 'react-icons/fa';
import './About.css';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const features = [
    {
      icon: <FaCode />,
      title: "Clean Code",
      description: "Writing maintainable and scalable code that follows best practices."
    },
    {
      icon: <FaPalette />,
      title: "Creative Design",
      description: "Creating beautiful and intuitive user interfaces that users love."
    },
    {
      icon: <FaRocket />,
      title: "Fast Performance",
      description: "Optimizing applications for speed and efficiency across all devices."
    },
    {
      icon: <FaUsers />,
      title: "User Focused",
      description: "Building solutions that solve real problems for real users."
    }
  ];

  return (
    <section id="about" className="about">
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>About Me</h2>
          <p className="section-subtitle">
            Passionate developer creating digital experiences that matter
          </p>
        </motion.div>

        <div className="about-content" ref={ref}>
          <motion.div 
            className="about-text"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3>Who I Am</h3>
            <p>
              I'm a passionate Full Stack Developer with a love for creating beautiful, 
              functional, and user-centered digital experiences. With a strong foundation 
              in both front-end and back-end development, I bring ideas to life through 
              clean code and innovative solutions.
            </p>
            <p>
              My journey in web development started with curiosity and has evolved into 
              a passion for building applications that not only look great but also 
              provide exceptional user experiences. I believe in writing code that's 
              not just functional, but also maintainable and scalable.
            </p>
            <p>
              When I'm not coding, you can find me exploring new technologies, 
              contributing to open-source projects, or sharing knowledge with the 
              developer community.
            </p>
          </motion.div>

          <motion.div 
            className="about-stats"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-number">1+</div>
                <div className="stat-label">Years Experience</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">2+</div>
                <div className="stat-label">Projects Completed</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">3+</div>
                <div className="stat-label">Happy Clients</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100%</div>
                <div className="stat-label">Client Satisfaction</div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="features-grid"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {features.map((feature, index) => (
            <motion.div 
              key={feature.title}
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="feature-icon">
                {feature.icon}
              </div>
              <h4>{feature.title}</h4>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
